import type { ReactNode, Ref, RefObject } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { ImageAssetType } from './carousel.types';
import RemoveIcon from '../../../asset/icons/photo-viewer/remove.svg?react';
import { Textarea } from '@/components/ui/textarea';

interface AssetCardListProps {
  assetList: ImageAssetType[];
  inputMap: Record<string, string>;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  requestType: 'service' | 'support';
  mode: string;
  onInputChange: (uuid: string, val: string) => void;
  onRemoveAsset: (uuid: string) => void;
  onRemoveFromCart?: () => void;
  /**
   * Sub-lg presentation (QA round 5, spec §1.11): each card stacks — a
   * thumbnail/caption/remove header row with the note textarea full-width
   * beneath it. The default side-by-side row keeps the desktop dialog
   * pixel-unchanged. Chosen by the host modal (which owns the dialog/sheet
   * branch), not by CSS, so the textarea exists exactly once per card.
   */
  stacked?: boolean;
  /**
   * The dialog presentation caps the list at 50vh and scrolls it internally.
   * The bottom sheet scrolls its whole body instead — a nested scroller
   * inside it reads as a stuck page — so the host passes false there.
   */
  constrainHeight?: boolean;
}

export default function AssetCardList({
  assetList,
  inputMap,
  inputRef,
  requestType,
  mode,
  onInputChange,
  onRemoveAsset,
  onRemoveFromCart,
  stacked = false,
  constrainHeight = true
}: AssetCardListProps) {
  const renderThumb = (asset: ImageAssetType, isCompleted: boolean) => (
    <div className="relative shrink-0">
      <img
        src={asset?.caches?.['300w']}
        alt="Asset thumbnail"
        className={cn(
          'object-cover rounded-md ring-1 ring-black/5',
          stacked ? 'w-24 h-16' : 'w-36 h-24'
        )}
      />
      {isCompleted && (
        <span
          className={cn(
            'absolute -top-1.5 -right-1.5',
            'flex h-5 w-5 items-center justify-center',
            'rounded-full bg-success text-white shadow-sm',
            'ring-2 ring-background'
          )}
          aria-label="Details added"
        >
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      )}
    </div>
  );

  const renderNoteInput = (asset: ImageAssetType, index: number) => (
    <Textarea
      id={`details-${asset.assetUUID}`}
      name={`details-${asset.assetUUID}`}
      rows={stacked ? 3 : 2}
      maxLength={350}
      placeholder="Enter Request Details Here"
      value={inputMap[asset.assetUUID!] || ''}
      onChange={(e) => onInputChange(asset.assetUUID!, e.target.value)}
      ref={index === 0 ? (inputRef as Ref<HTMLTextAreaElement>) : undefined}
      required
    />
  );

  const renderRemove = (asset: ImageAssetType, showRemove: boolean) =>
    showRemove && (
      <div className="shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remove"
          tabIndex={-1}
          className={cn(
            // Stacked (touch) cards keep the button visible — hover-reveal
            // controls are invisible AND unreachable on touch (same hazard
            // the round-4 image-card fix closed).
            !stacked && 'opacity-0 transition-opacity',
            !stacked && 'group-hover:opacity-100 focus-visible:opacity-100'
          )}
          onClick={() => {
            if (requestType === 'service' && mode === 'edit' && onRemoveFromCart) {
              onRemoveFromCart();
            } else {
              onRemoveAsset(asset.assetUUID!);
            }
          }}
        >
          <RemoveIcon className="h-5 w-5" />
        </Button>
      </div>
    );

  const renderCaption = (asset: ImageAssetType): ReactNode => (
    <Typography
      variant="heading-md"
      className={cn(stacked && 'flex-1 min-w-0 break-words')}
    >
      {asset?.caption}
    </Typography>
  );

  return (
    <div
      className={cn(
        constrainHeight && 'max-h-[50vh] overflow-y-auto',
        'flex flex-col gap-2',
        'px-2 pt-2 pb-3'
      )}
    >
      {assetList.map((asset, index) => {
        const isCompleted =
          !!asset.assetUUID && !!inputMap[asset.assetUUID]?.trim();
        const showRemove =
          (requestType === 'support' && assetList.length > 1) ||
          (requestType === 'service' && mode === 'edit');

        if (stacked) {
          return (
            <div
              key={asset.assetUUID || index}
              className="flex flex-col gap-3 bg-background border border-border rounded-lg p-3"
            >
              <div className="flex items-center gap-3">
                {renderThumb(asset, isCompleted)}
                {renderCaption(asset)}
                {renderRemove(asset, showRemove)}
              </div>
              {renderNoteInput(asset, index)}
            </div>
          );
        }

        return (
          <div
            key={asset.assetUUID || index}
            className={cn(
              'group flex items-center gap-4',
              'bg-background border border-border rounded-lg p-3',
              'transition-shadow duration-200',
              'hover:shadow-sm'
            )}
          >
            {renderThumb(asset, isCompleted)}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              {renderCaption(asset)}
              {renderNoteInput(asset, index)}
            </div>
            {renderRemove(asset, showRemove)}
          </div>
        );
      })}
    </div>
  );
}
